// ProductsPanel — owns the Products section's cross-row data and its two
// dialogs (My Account C1, task T7b, design.md AC5/AC6/AC7; rewritten by the
// account-area redesign Task 7, Amendment 2026-10-02 F6/O3/O9).
//
// AccountCenter.tsx mounts THIS component for the Products section;
// ProductsTab renders the rows through the panel-seam props (stores,
// associations, RMA status) — see ProductsTab's header for the rule-48 note
// on the replaced extension slots.
//
// Owns: the customer's RMA requests and the "Request a return" dialog.
// Fetched ONCE here; the row controls are purely presentational.
//
// The two dialogs (both amendment F6/O3 — the row can only point at them):
//   - "Add store" (create-then-assign): opened when a row's store select
//     chooses "+ Create new store…". StoreForm creates; onSaved assigns the
//     NEW store to the row that asked and closes. Cancel closes without any
//     POST, and because the row select is controlled by the association, the
//     customer's pending selection is exactly what it was.
//   - "Request a return": opened by a row's ⋯ menu (hardware only; hidden
//     entirely while an open request exists — the Status badge then answers
//     "what happened to my return"). RmaRequestForm creates; onCreated
//     appends and closes; the badge derives from this list.
//
// ⚠️ AMENDED 2026-10-02 (account-area redesign, Task 4, operator feedback F7).
// ORIGINAL: also owned the "Manage stores" sub-view toggle (design.md AC7).
// CURRENT: Stores is its own account section (StoresPanel.tsx), so the link,
// the sub-view and the store CRUD handlers are gone from here. The stores
// list stays: the row's store picker and the create-then-assign flow need it.
//
// ⚠️ AMENDED 2026-10-10 (merge-review M2, TD-583). ORIGINAL: the panel loaded all four
// of its reads unconditionally, on the assumption that a member who reached the section
// (then gated by manage-stores) could read everything in it. CURRENT: the section is
// gated by its own area, view-products (operator answer 2026-10-10: "New products
// permission" -- "A dedicated ACL area. Needs a seed migration and a column in the
// portal permission matrix."), and the data inside is governed by four OTHER areas, so
// a member can hold view-products and lack any of them. The panel takes the self-view
// and reads only what the member may read (`productsAccess`): a denied read is never
// sent (a 403 would surface as the load-error banner -- the "broken state" the ruling
// forbids) and the control that needed it is hidden. The server's per-endpoint refusals
// stay the control; this is presentation.
//
// ⚠️ AMENDED 2026-10-27 (TD-671 Task 8). ORIGINAL: the panel also loaded the
// customer's stores and the (product -> store) associations, resolved a
// (product -> order_line_ids) map through a second /me/orders fetch, owned the
// per-row store PUT and an "Add store" create-then-assign dialog. CURRENT: the
// store select is retired (F11), so the stores read, the associations read, the
// PUT and the dialog are gone (nothing consumes them here; the move dialog of a
// later task loads what it needs). The order-line ids come from the owned item
// itself: ProductsTab hands the item's `lines` to onRequestReturn and the dialog
// lets the customer pick the purchase (RmaRequestForm `lines`), capping the
// quantity input at that line's `quantity`. The panel's only read is now the
// RMA list.
//
// Inline styles only — no Tailwind.

import { useState, useEffect, useCallback, useMemo } from "react";
import { useEmbedTokens, EmbedBanner, EmbedButton, EmbedCard, EmbedDialog } from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { ProductsTab } from "./ProductsTab";
import { RmaRequestForm, returnableLines } from "./RmaRequestControl";
import { listRMARequests, OPEN_RMA_STATUSES } from "./rma-api";
import type { RMARequestWire } from "./rma-api";
import type { OwnedLineWire } from "./owned-products-api";
import { productsAccess } from "./account-sections";
import type { SelfViewWire } from "./membership-api";

function messageOf(reason: unknown, fallback: string): string {
  return reason instanceof ApiError ? reason.message : fallback;
}

/** The status a row's ⋯ gate and Status badge surface: the OPEN request when
 *  one exists (the menu hides), otherwise the latest request's status (the
 *  badge keeps the last outcome visible), else null. listRMARequests serves
 *  newest-first (ORDER BY created_at DESC), so the latest terminal request is
 *  requests[0]; an in-session create is APPENDED by handleRmaCreated but is
 *  always open, so it reaches the predicate above, never this fallback. */
function surfaceStatus(requests: RMARequestWire[]): string | null {
  if (requests.length === 0) return null;
  const open = requests.find((r) => OPEN_RMA_STATUSES.has(r.status));
  return (open ?? requests[0]).status;
}

export interface ProductsPanelProps {
  /** The shell's loaded self-view. Null/undefined = unloaded = everything permitted (the
   *  fail-open presentation rule of `isAreaPermitted`; the server's refusals are the control). */
  selfView?: SelfViewWire | null;
}

export function ProductsPanel({ selfView = null }: ProductsPanelProps = {}) {
  const t = useEmbedTokens();
  // Keyed on the four derived booleans, NOT the self-view object: a self-view
  // refresh (onSelfViewStale -> loadSelfView) yields a fresh object even when no
  // permission moved, and keying on identity re-fired load() -- the read plus a
  // loading flash -- for nothing.
  const derived = productsAccess(selfView);
  const access = useMemo(
    () => ({ orders: derived.orders, subscriptions: derived.subscriptions, stores: derived.stores, returns: derived.returns }),
    [derived.orders, derived.subscriptions, derived.stores, derived.returns],
  );

  const [loadError, setLoadError] = useState<string | null>(null);
  const [rmaRequests, setRmaRequests] = useState<RMARequestWire[]>([]);

  // The "Request a return" dialog: the product, the ⋯ trigger (focus returns to
  // it on close) and the owned item's returnable lines to choose between.
  const [returnDialog, setReturnDialog] = useState<{
    productId: string;
    trigger: HTMLElement;
    lines: OwnedLineWire[];
  } | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    // TD-583: a read the member's role may not make is NEVER sent. The RMA list
    // decorates owned rows, so it needs view-orders (rows exist) AND manage-rma.
    // A skipped read resolves to its empty value, never to a failure.
    if (!(access.orders && access.returns)) {
      setRmaRequests([]);
      return;
    }
    try {
      setRmaRequests(await listRMARequests());
    } catch (err) {
      setLoadError(messageOf(err, "Failed to load return requests"));
    }
  }, [access]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRmaCreated = useCallback((created: RMARequestWire) => {
    setRmaRequests((prev) => [...prev, created]);
  }, []);

  const rmaStatusFor = useCallback(
    (productId: string) => surfaceStatus(rmaRequests.filter((r) => r.product_id === productId)),
    [rmaRequests],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
      {loadError && (
        <EmbedCard>
          <EmbedBanner tone="error">{loadError}</EmbedBanner>
          <div style={{ marginTop: t.spacing.md }}>
            <EmbedButton type="button" variant="secondary" onClick={load}>
              Retry
            </EmbedButton>
          </div>
        </EmbedCard>
      )}

      <ProductsTab
        access={access}
        rmaStatusFor={rmaStatusFor}
        onRequestReturn={(productId, trigger, lines) => setReturnDialog({ productId, trigger, lines: returnableLines(lines) })}
      />

      {/* Request a return — the ⋯ menu's single item lands here. The form's
          verbatim 409 contract (never retried) lives in RmaRequestForm. */}
      <EmbedDialog
        open={returnDialog != null}
        onClose={() => setReturnDialog(null)}
        title="Request a return"
        size="small"
        returnFocusTo={returnDialog?.trigger ?? null}
      >
        {returnDialog && (
          <RmaRequestForm
            productId={returnDialog.productId}
            orderLineId={undefined}
            lines={returnDialog.lines}
            onCreated={handleRmaCreated}
            onClose={() => setReturnDialog(null)}
          />
        )}
      </EmbedDialog>
    </div>
  );
}
